const API_URL = process.env.REACT_APP_INSIGHTS_API_URL || "/api/insights.php";
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const BLOCK_TYPES = new Set(["paragraph", "heading", "pullquote", "bullets", "divider"]);

const isText = (value, max) => typeof value === "string" && value.trim().length > 0 && value.length <= max;
const isInternalPath = (value) => typeof value === "string" && /^\/[a-z0-9]+(?:[/-][a-z0-9]+)*\/?$/.test(value);

const normaliseSegments = (segments, max) => {
  if (!Array.isArray(segments) || segments.length === 0 || segments.length > 100) return null;
  const cleaned = segments.map((segment) => {
    if (!segment || typeof segment !== "object" || !isText(segment.text, 4000)) return null;
    if (segment.bold !== undefined && typeof segment.bold !== "boolean") return null;
    if (segment.italic !== undefined && typeof segment.italic !== "boolean") return null;
    if (segment.href !== undefined && !isInternalPath(segment.href)) return null;
    return {
      text: segment.text,
      ...(segment.bold ? { bold: true } : {}),
      ...(segment.italic ? { italic: true } : {}),
      ...(segment.href ? { href: segment.href } : {}),
    };
  });
  if (cleaned.some((segment) => segment === null)) return null;
  return isText(cleaned.map((segment) => segment.text).join(""), max) ? cleaned : null;
};

const normaliseBlock = (block) => {
  if (typeof block === "string" && isText(block, 12000)) return { type: "paragraph", text: block };
  if (!block || typeof block !== "object" || !BLOCK_TYPES.has(block.type)) return null;
  if (block.type === "divider") return { type: "divider" };
  if (block.type === "heading") {
    const level = block.level === undefined ? 2 : block.level;
    return isText(block.text, 500) && (level === 2 || level === 3) ? { type: "heading", level, text: block.text } : null;
  }
  if (block.type === "bullets") {
    if (block.ordered !== undefined && typeof block.ordered !== "boolean") return null;
    if (!Array.isArray(block.items) || block.items.length > 50) return null;
    const items = block.items.map((item) => typeof item === "string" ? [{ text: item }] : normaliseSegments(item, 2000));
    return items.some((item) => item === null) ? null : { type: "bullets", ordered: block.ordered === true, items };
  }
  if (block.segments !== undefined) {
    const segments = normaliseSegments(block.segments, 12000);
    return segments ? { type: block.type, segments } : null;
  }
  return isText(block.text, 12000) ? { type: block.type, text: block.text } : null;
};

export const normaliseArticle = (raw, includeBody = true) => {
  if (!raw || typeof raw !== "object" || !SLUG.test(raw.slug || "")) return null;
  if (!isText(raw.theme, 100) || !isText(raw.date, 10) || !isText(raw.title, 240) || !isText(raw.excerpt, 600)) return null;
  if (!isText(raw.seoTitle, 240) || !isText(raw.seoDescription, 600) || raw.published !== true) return null;
  for (const [field, max] of [["socialTitle", 240], ["socialDescription", 600], ["publication", 100], ["author", 200]]) {
    if (raw[field] !== undefined && !isText(raw[field], max)) return null;
  }
  const hasCta = raw.ctaLabel !== undefined || raw.ctaHref !== undefined;
  if (hasCta && (!isText(raw.ctaLabel, 120) || !isInternalPath(raw.ctaHref))) return null;
  if (raw.redirectFrom !== undefined && (!isInternalPath(raw.redirectFrom) || raw.redirectFrom === `/insights/${raw.slug}`)) return null;
  if (raw.legacy !== undefined && typeof raw.legacy !== "boolean") return null;
  const legacy = raw.legacy === true;
  const hasBannerMetadata = raw.banner !== undefined || raw.bannerAlt !== undefined;
  if (!legacy && !hasBannerMetadata) return null;
  if (hasBannerMetadata && (raw.banner !== `${raw.slug}-banner.webp` || !isText(raw.bannerAlt, 300))) return null;
  const article = { ...raw };
  if (includeBody) {
    if (!Array.isArray(raw.body)) return null;
    const body = raw.body.map(normaliseBlock);
    if (body.some((block) => block === null)) return null;
    article.body = body;
  }
  return article;
};

const request = async (url, signal) => {
  const response = await fetch(url, { method: "GET", headers: { Accept: "application/json" }, signal });
  if (!response.ok) {
    const error = new Error(response.status === 404 ? "not_found" : "unavailable");
    error.status = response.status;
    throw error;
  }
  return response.json();
};

export const fetchInsights = async (signal) => {
  const data = await request(API_URL, signal);
  if (!Array.isArray(data.articles)) throw new Error("invalid_response");
  return data.articles.map((article) => normaliseArticle(article, false)).filter(Boolean).sort((a, b) => b.date.localeCompare(a.date));
};

export const fetchInsight = async (slug, signal) => {
  if (!SLUG.test(slug || "")) {
    const error = new Error("not_found");
    error.status = 404;
    throw error;
  }
  const data = await request(`${API_URL}?slug=${encodeURIComponent(slug)}`, signal);
  const article = normaliseArticle(data.article, true);
  if (!article) throw new Error("invalid_response");
  return article;
};

export const insightBannerUrl = (banner) => `/content/insights/${encodeURIComponent(banner)}`;
